import React, {
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  message
} from 'antd';
import { getRoleNames, type RoleName } from '../../../service/user.service';
import {
  addWorkspaceMember,
  listMemberCandidates,
  type MemberCandidate
} from '../../../service/workspaceMembers.service';
import { grantExceedsFrom403 } from '../../../service/capability.service';
import { moduleLabel } from '../../../config/capabilities';

type AddForm = { userId: string; roleKey: string };

/** How long to wait after the last keystroke before asking the server for candidates. */
const CANDIDATE_SEARCH_DELAY_MS = 250;

const roleValue = (role: RoleName) => role.custom_role_id ? `custom:${role.custom_role_id}` : `builtin:${role.name}`;
const roleBody = (value: string) => value.startsWith('custom:')
  ? { custom_role_id: Number(value.slice('custom:'.length)) }
  : { role: value.slice('builtin:'.length) };

/** A 403 GRANT_EXCEEDS_GRANTOR as a sentence listing the modules the ceiling refused. */
const grantMessage = (error: any): string | null => {
  const modules = grantExceedsFrom403(error);
  if (!modules) return null;
  return `You cannot grant more than you hold. Exceeds your access on: ${modules.map(moduleLabel).join(', ') || 'unknown modules'}.`;
};

/**
 * Workspaces do not invite: somebody the organisation already has is picked and given a
 * workspace role. New people are invited to the organisation instead.
 */
const AddFromOrganisation: React.FC<{
  open: boolean;
  workspaceId: number;
  workspaceName: string;
  onClose: () => void;
  onAdded: () => void;
}> = ({ open, workspaceId, workspaceName, onClose, onAdded }) => {
  const [form] = Form.useForm<AddForm>();
  const [roles, setRoles] = useState<RoleName[]>([]);
  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // Answers can arrive out of order while the operator types; only the last request counts.
  const request = useRef(0);

  const search = (value: string, immediate = false) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const mine = ++request.current;
      setLoading(true);
      try {
        const next = await listMemberCandidates(workspaceId, value.trim());
        if (mine === request.current) setCandidates(next);
      } catch (error: any) {
        if (mine === request.current) {
          setCandidates([]);
          message.error(error?.response?.data?.detail ?? 'People in your organisation could not be loaded.');
        }
      } finally {
        if (mine === request.current) setLoading(false);
      }
    }, immediate ? 0 : CANDIDATE_SEARCH_DELAY_MS);
  };

  useEffect(() => {
    if (!open) return;
    setCandidates([]);
    search('', true);
    getRoleNames().then(setRoles).catch(() => setRoles([]));
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspaceId]);

  const submit = async (values: AddForm) => {
    setAdding(true);
    try {
      await addWorkspaceMember(workspaceId, {
        user_id: values.userId,
        ...roleBody(values.roleKey)
      });
      message.success('Member added to this workspace.');
      form.resetFields();
      onAdded();
      onClose();
    } catch (error: any) {
      message.error(grantMessage(error) ?? error?.response?.data?.detail ?? 'The member could not be added.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Modal
      title="Add from organisation"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={submit}
      >
        <Form.Item label="Workspace">
          <Input value={workspaceName} disabled />
        </Form.Item>
        <Form.Item
          label="Person"
          name="userId"
          rules={[{
            required: true,
            message: 'Choose a person from your organisation'
          }]}
          extra="People who already belong to this organisation and are not in this workspace yet. To bring in someone new, invite them to the organisation."
        >
          <Select
            showSearch
            filterOption={false}
            loading={loading}
            placeholder="Search by name or email"
            onSearch={(value) => search(value)}
            notFoundContent={loading ? 'Loading…' : 'Nobody else in your organisation to add'}
            options={candidates.map((candidate) => ({
              value: candidate.id,
              label: candidate.name === candidate.email ? candidate.email : `${candidate.name} (${candidate.email})`
            }))}
          />
        </Form.Item>
        <Form.Item
          label="Role"
          name="roleKey"
          rules={[{ required: true }]}
        >
          <Select
            options={roles.filter((role) => role.assignable !== false).map((role) => ({
              value: roleValue(role),
              label: role.name
            }))}
          />
        </Form.Item>
        <Button
          htmlType="submit"
          type="primary"
          block
          loading={adding}
        >Add to workspace</Button>
      </Form>
    </Modal>
  );
};

export default AddFromOrganisation;
